/**
 * Centralized analytics tracking mixin
 *
 * Each method sends the same moment to GTM and to PostHog. GTM feeds GA4 for
 * acquisition; PostHog holds the per-user funnel. Either one can be off, so
 * both calls guard themselves.
 *
 * We validate if this.gtm? as the plugin may not be loaded
 *
 * Every push names its own event: without one the library sends them all as
 * `interaction`, which no trigger can tell apart.
 *
 * Nothing here may carry anything identifying. Analytics is a third party and
 * a phone number or an email sent once cannot be taken back.
 *
 * @link https://www.npmjs.com/package/@gtm-support/vue-gtm
 */

import { useLocalStorage } from "@vueuse/core"
import { capture, scrubUrl } from "@/plugins/posthog"

export default {
    methods: {
        /**
         * First open is really supposed to be the first time ever opening the app.
         *
         * This method tracks the first landing event for the user using local storage.
         */
        trackFirstLanding(): void {
            const now          = (new Date()).toISOString()
            const firstLanding = useLocalStorage('firstLanding', now)
            if (firstLanding.value === now) {
                firstLanding.value = now
                this.$gtm?.trackEvent({
                    event:      'first_landing',
                    pageUrl:    scrubUrl(this.$route.fullPath),
                    deviceType: this.$vuetify.display.smAndDown ? 'mobile' : 'desktop',
                })
                capture('first_landing', this.trackingContext())
            }
        },

        /**
         * Sign up is when they actually sign up to create an account.
         */
        trackSignUp(): void {
            this.$gtm?.trackEvent({
                event:      'sign_up',
                pageUrl:    scrubUrl(this.$route.fullPath),
                deviceType: this.$vuetify.display.smAndDown ? 'mobile' : 'desktop',
            })
            capture('sign_up', this.trackingContext())
        },

        /**
         * Just right after confirming the payment was confirmed.
         */
        trackPaymentConfirmation(planName?: string): void {
            this.$gtm?.trackEvent({
                event:      'payment_confirmation',
                pageUrl:    scrubUrl(this.$route.fullPath),
                deviceType: this.$vuetify.display.smAndDown ? 'mobile' : 'desktop',
                plan:       planName ?? 'unknown',
            })
            capture('payment_confirmation', {
                ...this.trackingContext(),
                plan: planName ?? 'unknown',
            })
        },

        trackingContext(): Record<string, unknown> {
            return {
                page_url:    scrubUrl(this.$route.fullPath),
                device_type: this.$vuetify.display.smAndDown ? 'mobile' : 'desktop',
            }
        },
    }
}
